import React, { useState, useEffect, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { BarChart2, Loader2, Clock, Calendar } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { TopProgressBar } from "@/components/ui/top-progress-bar";
import { updateMultiplePostsMetrics } from "@/utils/engagementMetrics";
import { KolDataWithDates } from '@/types/table';
import { SearchableDataTable } from "@/components/table/SearchableDataTable";
import { AddKolTrackerDataDialog } from './AddKolTrackerDataDialog';
import { useTheme } from "@/contexts/ThemeContext";
import { useAuth } from "@/contexts/AuthContext";
import { useCascadingLogic } from './utils/cascadingLogic';
import { useKolPostingLinksCellRenderers } from './renderers/KolPostingLinksCellRenderers';
import { createKolPostingLinksColumns } from './config/kolPostingLinksColumns';
import { CreatorInfo } from './CreatorInfo';
import { KolPostingLinksFilters } from './filters/KolPostingLinksFilters';
import { FilterValues } from '@/hooks/useKolPostingLinksFilters';
import { KolPostingLinksExportDialog } from './export/KolPostingLinksExportDialog';
import { 
  CONTENT_TYPE_OPTIONS, 
  PLATFORM_OPTIONS, 
  TYPE_OPTIONS, 
  POST_TYPE_OPTIONS, 
  TIER_OPTIONS, 
  LANGUAGE_OPTIONS 
} from './constants/kolPostingLinksConstants';

interface Option {
  value: string;
  label: string;
}

interface KolData {
  id: string;
  name: string;
  type: string;
  channel: string;
  platform: string;
  post_type: string;
  post_link: string;
  content_views: number;
  expense: number;
  business_unit: string;
  campaign_bu: string;
  pic: string[] | null;
  posting_date: string;
  language: string;
  tier: string;
  integration_enable: boolean;
  updated_date: string | null;
  engagement_metrics: string | null;
  kol_db_id?: string;
  created_at?: string;
  updated_at?: string;
}

interface KolPostingLinksTableProps {
  data: KolData[];
  isLoading: boolean;
  onDataChange?: () => void;
  hasFullAccess?: boolean;
  currentUserName?: string;
  totalCount?: number;
  searchTerm?: string;
  onSearchChange?: (searchTerm: string) => void;
  onSearch?: () => void;
  currentPage?: number;
  onPageChange?: (newPage: number) => void;
  pageSize?: number;
  onPageSizeChange?: (size: number) => void;
  // Filter props passed down from parent
  filters?: FilterValues;
  onFilterChange?: (field: keyof FilterValues, value: any) => void;
  onClearFilters?: () => void;
  hasActiveFilters?: boolean;
  // Add sorting props
  sortByCreatedTime?: boolean;
  onSortChange?: (sortByCreatedTime: boolean) => void;
  // Database-level sorting props
  dbSortColumn?: string | null;
  dbSortDirection?: 'asc' | 'desc' | null;
  onDbSort?: (column: string | null, direction: 'asc' | 'desc' | null) => void;
}

export function KolPostingLinksTable({ 
  data, 
  isLoading, 
  onDataChange,
  hasFullAccess = true,
  currentUserName = '',
  totalCount = 0,
  searchTerm = "",
  onSearchChange,
  onSearch,
  currentPage = 1,
  onPageChange,
  pageSize = 50,
  onPageSizeChange,
  // Filter props
  filters,
  onFilterChange,
  onClearFilters,
  hasActiveFilters = false,
  // Sort props
  sortByCreatedTime = true,
  onSortChange,
  // Database sort props
  dbSortColumn,
  dbSortDirection,
  onDbSort
}: KolPostingLinksTableProps) {
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isUpdatingMetrics, setIsUpdatingMetrics] = useState(false);
  const [updateProgress, setUpdateProgress] = useState<{
    current: number;
    total: number;
    url: string;
  } | null>(null);
  const { toast } = useToast();
  const [picOptions, setPicOptions] = useState<Option[]>([]);
  const [kolDbList, setKolDbList] = useState<any[]>([]);
  const [contentTypeOptions, setContentTypeOptions] = useState<Record<string, string[]>>({});
  const [topicsBusinessUnits, setTopicsBusinessUnits] = useState<any[]>([]);
  const { theme } = useTheme();
  const { hasBuAccess } = useAuth();

  // Reset updating state on component mount to prevent stuck state
  useEffect(() => {
    setIsUpdatingMetrics(false);
    setUpdateProgress(null);
  }, []);

  // Create Content ID options from topics_business_units
  const CONTENT_ID_OPTIONS = useMemo(() => {
    const contentIds = topicsBusinessUnits
      .filter(item => item.content_id && item.content_id.trim() !== '')
      .map(item => ({
        value: item.content_id,
        label: item.content_id,
        description: item.description,
        tags: Array.isArray(item.tags) ? item.tags : [],
        pic: Array.isArray(item.pic) ? item.pic : []
      }));
    
    console.log("Available Content ID options:", contentIds);
    return contentIds;
  }, [topicsBusinessUnits]);

  // Create Business Unit options - but this is now unused due to hidden column
  const BUSINESS_UNIT_OPTIONS = useMemo(() => {
    const businessUnits = topicsBusinessUnits
      .filter(item => item.type === 'BU')
      .map(bu => bu.name);
    
    return [...businessUnits, 'Others'];
  }, [topicsBusinessUnits]);

  const {
    getAllCampaignsAndTopics
  } = useCascadingLogic(topicsBusinessUnits);

  const getAllCampaignsAndTopicsWithIcons = () => {
    // Return Content ID options for multi-select
    return CONTENT_ID_OPTIONS.map(option => ({
      value: option.value,
      label: option.label,
      type: 'Content ID' as 'Campaign' | 'Topic' // Type assertion to fix the type error
    }));
  };

  // Transform data without sorting here - let parent handle sorting
  const transformedData = useMemo<KolDataWithDates[]>(() => {
    console.log('🔍 Transform data input sample:', data.slice(0, 2));
    const transformed = data.map(item => ({
      ...item,
      business_unit: item.business_unit || '',
      posting_date: item.posting_date ? new Date(item.posting_date + 'T00:00:00') : null,
      updated_date: item.updated_date ? new Date(item.updated_date) : null,
      pic: Array.isArray(item.pic) ? item.pic : (item.pic ? [item.pic] : []),
      campaign_bu: Array.isArray(item.campaign_bu) ? item.campaign_bu : (item.campaign_bu ? [item.campaign_bu] : []),
      created_at: item.created_at ? new Date(item.created_at) : null,
      updated_at: item.updated_at ? new Date(item.updated_at) : null,
    }));
    console.log('🔍 Transform data output sample:', transformed.slice(0, 2));
    return transformed;
  }, [data]);

  useEffect(() => {
    const fetchTopicsBusinessUnits = async () => {
      try {
        console.log("Fetching topics and business units...");
        const { data, error } = await supabase
          .from('topics_business_units')
          .select('*')
          .order('type', { ascending: true })
          .order('name', { ascending: true });
          
        if (error) throw error;
        
        if (data) {
          console.log("Fetched topics_business_units:", data);
          setTopicsBusinessUnits(data);
        }
      } catch (err) {
        console.error("Error fetching topics and business units:", err);
      }
    };
    
    fetchTopicsBusinessUnits();
  }, []);
  
  useEffect(() => {
    const fetchKolDb = async () => {
      try {
        const { data, error } = await supabase
          .from('kol_db')
          .select('id, creator_name, content_type, social_media_type, social_media_handler, region, profile_image_url');
          
        if (error) throw error;
        
        if (data) {
          const contentTypeMap: Record<string, string[]> = {};
          data.forEach(kol => {
            const contentTypeArray = Array.isArray(kol.content_type) ? kol.content_type : [];
            if (contentTypeArray.length > 0) {
              const contentTypes = contentTypeArray.map(item => String(item));
              contentTypeMap[kol.id] = contentTypes;
            } else {
              contentTypeMap[kol.id] = [];
            }
          });
          
          setKolDbList(data);
          setContentTypeOptions(contentTypeMap);
        }
      } catch (err) {
        console.error("Error fetching KOL DB data:", err);
      }
    };
    
    fetchKolDb();
  }, []);
  
  useEffect(() => {
    const fetchPics = async () => {
      try {
        const { data, error } = await supabase
          .from('kol_pic')
          .select('name')
          .order('name');
        
        if (error) throw error;
        
        if (data) {
          const options = data.map(pic => ({
            value: pic.name,
            label: pic.name
          }));
          setPicOptions(options);
        }
      } catch (err) {
        console.error('Error fetching PIC options:', err);
      }
    };
    
    fetchPics();
  }, []);

  // Define platforms that support integration
  const INTEGRATION_SUPPORTED_PLATFORMS = ['twitter', 'youtube', 'telegram'];

  // Enhanced updateRow function with debug logging
  const updateRow = async (rowId: string, field: keyof KolDataWithDates, value: any) => {
    try {
      setIsProcessing(true);
      
      console.log(`🔄 Updating ${rowId} field ${String(field)} to:`, value);
      
      const updateData: any = {
        [field]: value
      };
      
      // Handle posting_date field specially to avoid timezone issues
      if (field === 'posting_date' && value instanceof Date) {
        // Convert to local date string (YYYY-MM-DD) without timezone conversion
        const year = value.getFullYear();
        const month = String(value.getMonth() + 1).padStart(2, '0');
        const day = String(value.getDate()).padStart(2, '0');
        updateData[field] = `${year}-${month}-${day}`;
      }
      
      const { error } = await supabase
        .from('kol_tracker')
        .update(updateData)
        .eq('id', rowId);

      if (error) throw error;
      
      toast({
        title: "Success",
        description: "Record updated successfully",
      });
      
      // Refresh data immediately
      if (onDataChange) onDataChange();
      
    } catch (error) {
      console.error("❌ Error updating row:", error);
      toast({
        title: "Error",
        description: "Failed to update record",
        variant: "destructive"
      });
      throw error; // Re-throw so EditableCell can handle it
    } finally {
      setIsProcessing(false);
    }
  };

  const getContentTypeOptionsForKol = (kolDbId: string | undefined): string[] => {
    if (!kolDbId) return TYPE_OPTIONS;
    
    const options = contentTypeOptions[kolDbId];
    if (!options || options.length === 0) return TYPE_OPTIONS;
    
    return Array.isArray(options) ? 
      options.map(item => typeof item === 'string' ? item : String(item)) : 
      TYPE_OPTIONS;
  };

  const getKolDbInfo = (kolDbId: string | undefined) => {
    if (!kolDbId) return null;
    
    return kolDbList.find(kol => kol.id === kolDbId);
  };

  const getTypeVariant = (type: string): "blue" | "red" | "purple" | "green" | "yellow" => {
    if (!type) return "yellow";
    
    const lowerType = type.toLowerCase();
    
    if (lowerType.includes('twitter')) return "blue";
    if (lowerType.includes('youtube')) return "red";
    if (lowerType.includes('telegram')) return "purple";
    if (lowerType.includes('blog') || lowerType.includes('pr') || lowerType.includes('ama') || lowerType.includes('naver')) return "green";
    
    return "yellow";
  };

  const handleDeleteSelected = async () => {
    try {
      const { error } = await supabase
        .from('kol_tracker')
        .delete()
        .in('id', selectedRows);

      if (error) throw error;

      toast({
        title: "Success",
        description: `${selectedRows.length} record(s) deleted successfully`,
      });

      setSelectedRows([]);
      
      if (onDataChange) onDataChange();
    } catch (error) {
      console.error("Error deleting rows:", error);
      toast({
        title: "Error",
        description: "Failed to delete selected records",
        variant: "destructive"
      });
    }
  };

  const handleEnableIntegrationSelected = async () => {
    try {
      // Filter selected rows to only include supported platforms
      const selectedData = data.filter(row => selectedRows.includes(row.id));
      const supportedRows = selectedData.filter(row => {
        const platform = (row.platform || '').toLowerCase();
        return INTEGRATION_SUPPORTED_PLATFORMS.includes(platform);
      });

      if (supportedRows.length === 0) {
        toast({
          title: "No supported platforms",
          description: "Selected rows don't contain Twitter, YouTube, or Telegram platforms",
          variant: "destructive"
        });
        return;
      }

      // Update integration_enable to true for supported platforms
      const { error } = await supabase
        .from('kol_tracker')
        .update({ integration_enable: true })
        .in('id', supportedRows.map(row => row.id));

      if (error) throw error;

      toast({
        title: "Success",
        description: `Integration enabled for ${supportedRows.length} record(s)`,
      });

      setSelectedRows([]);
      
      if (onDataChange) onDataChange();
    } catch (error) {
      console.error("Error enabling integration:", error);
      toast({
        title: "Error",
        description: "Failed to enable integration for selected records",
        variant: "destructive"
      });
    }
  };

  const handleDisableIntegrationSelected = async () => {
    try {
      // Filter selected rows to only include supported platforms
      const selectedData = data.filter(row => selectedRows.includes(row.id));
      const supportedRows = selectedData.filter(row => {
        const platform = (row.platform || '').toLowerCase();
        return INTEGRATION_SUPPORTED_PLATFORMS.includes(platform);
      });

      if (supportedRows.length === 0) {
        toast({
          title: "No supported platforms",
          description: "Selected rows don't contain Twitter, YouTube, or Telegram platforms",
          variant: "destructive"
        });
        return;
      }

      // Update integration_enable to false for supported platforms
      const { error } = await supabase
        .from('kol_tracker')
        .update({ integration_enable: false })
        .in('id', supportedRows.map(row => row.id));

      if (error) throw error;

      toast({
        title: "Success",
        description: `Integration disabled for ${supportedRows.length} record(s)`,
      });

      setSelectedRows([]);
      
      if (onDataChange) onDataChange();
    } catch (error) {
      console.error("Error disabling integration:", error);
      toast({
        title: "Error",
        description: "Failed to disable integration for selected records",
        variant: "destructive"
      });
    }
  };

  const updateEngagementMetrics = async () => {
    try {
      console.log('🚀 Starting engagement metrics update function...');
      
      // Force reset any stuck states first
      setIsUpdatingMetrics(false);
      setUpdateProgress(null);
      
      // Small delay to ensure state is reset
      await new Promise(resolve => setTimeout(resolve, 100));
      
      console.log('📊 Setting updating state to true');
      setIsUpdatingMetrics(true);
      setUpdateProgress({ 
        current: 0, 
        total: 0, 
        url: '' 
      });
      
      const postsToUpdate = selectedRows.length > 0
        ? data.filter(row => selectedRows.includes(row.id))
            .filter(row => row.integration_enable)
            .map(row => ({
              id: row.id,
              post_link: row.post_link || "",
              integration_enable: !!row.integration_enable,
              platform: row.platform || ''
            }))
        : data
            .filter(row => row.integration_enable)
            .map(row => ({
              id: row.id,
              post_link: row.post_link || "",
              integration_enable: !!row.integration_enable,
              platform: row.platform || ''
            }));
      
      console.log(`📊 Found ${postsToUpdate.length} posts to update metrics for`);
      
      if (postsToUpdate.length === 0) {
        console.log('⚠️ No posts found with integration enabled');
        toast({
          title: "No enabled integrations",
          description: "No rows with integration enabled were found to update.",
          variant: "destructive"
        });
        setIsUpdatingMetrics(false);
        setUpdateProgress(null);
        return;
      }
      
      setUpdateProgress({
        current: 0,
        total: postsToUpdate.length,
        url: ''
      });
      
      console.log('🔄 Starting updateMultiplePostsMetrics...');
      await updateMultiplePostsMetrics(postsToUpdate, (current, total, url) => {
        console.log(`📈 Progress: ${current}/${total} - ${url}`);
        setUpdateProgress({ current, total, url });
      });
      
      console.log('✅ Metrics update completed successfully');
      toast({
        title: "Metrics Updated",
        description: `Engagement metrics updated successfully for ${postsToUpdate.length} posts`,
      });
      
      if (onDataChange) {
        console.log('🔄 Refreshing data...');
        onDataChange();
      }
    } catch (error) {
      console.error("❌ Error updating engagement metrics:", error);
      toast({
        title: "Error",
        description: "Failed to update engagement metrics: " + (error instanceof Error ? error.message : "Unknown error"),
        variant: "destructive"
      });
    } finally {
      console.log('🏁 Cleaning up metrics update state');
      setIsUpdatingMetrics(false);
      setUpdateProgress(null);
    }
  };

  const handleUpdateMetricsClick = React.useCallback((event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    console.log('🎯 Update Engagement Metrics button clicked - executing update');
    updateEngagementMetrics();
  }, [selectedRows, data]);

  const handleToggleIntegration = (rowData: KolDataWithDates, checked: boolean) => {
    updateRow(rowData.id, 'integration_enable', checked);
  };

  const handlePicChange = (row: KolDataWithDates, selectedPics: string[]) => {
    updateRow(row.id, 'pic', selectedPics);
  };

  // Enhanced database sort handler with debug logging and proper column mapping
  const handleDbSort = (column: string | null, direction: 'asc' | 'desc' | null) => {
    console.log('🎯 KolPostingLinksTable: handleDbSort called');
    console.log('📊 Column:', column, 'Direction:', direction);
    
    // Verify column mapping - map frontend column IDs to database column names
    const columnMappings: Record<string, string> = {
      'name': 'name',
      'tier': 'tier', 
      'language': 'language',
      'type': 'type',
      'channel': 'channel',
      'platform': 'platform',
      'post_type': 'post_type',
      'post_link': 'post_link',
      'content_views': 'content_views', // Key mapping for Content Views
      'expense': 'expense', // Key mapping for Expense
      'campaign_bu': 'campaign_bu',
      'pic': 'pic',
      'posting_date': 'posting_date',
      'integration_enable': 'integration_enable'
    };
    
    let dbColumn = column;
    if (column && columnMappings[column]) {
      dbColumn = columnMappings[column];
      console.log('✅ Column mapping found:', { frontend: column, database: dbColumn });
    } else if (column) {
      console.warn('⚠️ No explicit mapping for column:', column, 'using as-is');
    }
    
    if (onDbSort) {
      console.log('✅ Calling parent onDbSort handler with:', { dbColumn, direction });
      onDbSort(dbColumn, direction);
    } else {
      console.warn('⚠️ No onDbSort handler provided');
    }
  };

  // Initialize cell renderers with editing disabled ONLY for BU Access users
  const cellRenderers = useKolPostingLinksCellRenderers({
    updateRow,
    handleToggleIntegration,
    handlePicChange,
    getContentTypeOptionsForKol,
    getKolDbInfo,
    getTypeVariant,
    getAllCampaignsAndTopics: getAllCampaignsAndTopicsWithIcons,
    picOptions,
    hasFullAccess,
    PLATFORM_OPTIONS,
    POST_TYPE_OPTIONS,
    TIER_OPTIONS,
    LANGUAGE_OPTIONS,
    CONTENT_TYPE_OPTIONS,
    isEditable: !hasBuAccess // Only disable editing for BU Access users
  });

  // Create columns - with corrected isEditable logic and debug logging
  console.log('🔍 Creating columns with config:', {
    hasFullAccess,
    hasBuAccess,
    isEditable: !hasBuAccess // Only disable editing for BU Access users
  });

  const columns = createKolPostingLinksColumns({
    ...cellRenderers,
    handleToggleIntegration,
    handlePicChange,
    getContentTypeOptionsForKol,
    getKolDbInfo,
    getTypeVariant,
    picOptions,
    hasFullAccess,
    updateRow,
    PLATFORM_OPTIONS,
    POST_TYPE_OPTIONS,
    TIER_OPTIONS,
    LANGUAGE_OPTIONS,
    CONTENT_TYPE_OPTIONS,
    CONTENT_ID_OPTIONS,
    isEditable: !hasBuAccess, // Only disable editing for BU Access users
    hasBuAccess // Pass hasBuAccess to columns
  });

  // Debug log column configuration
  console.log('🔍 Column configuration for sorting:');
  columns.forEach(col => {
    if (col.sortable) {
      console.log(`📊 Column "${col.id}" (${col.header}): sortable=${col.sortable}, accessorKey=${col.accessorKey}`);
    }
  });

  // Filter out hidden columns from being displayed
  const visibleColumns = useMemo(() => {
    return columns.filter(column => !column.hidden);
  }, [columns]);

  // Update the columns to include name change handler
  const columnsWithNameHandler = useMemo(() => {
    return visibleColumns.map(column => {
      if (column.id === 'name') {
        return {
          ...column,
          cell: (row: KolDataWithDates) => {
            const kolInfo = getKolDbInfo(row.kol_db_id);
            return (
              <div className="px-2 py-1 max-w-full overflow-hidden">
                <CreatorInfo 
                  name={row.name} 
                  handler={row.channel} 
                  platform={kolInfo?.social_media_type || row.platform} 
                  region={kolInfo?.region || null}
                  profileImageUrl={kolInfo?.profile_image_url || null}
                  editable={true}
                  onNameChange={(newName) => {
                    updateRow(row.id, 'name', newName);
                  }}
                />
              </div>
            );
          }
        };
      }
      return column;
    });
  }, [visibleColumns, getKolDbInfo, updateRow]);

  // Handle sort change
  const handleSortChange = (checked: boolean) => {
    if (onSortChange) {
      onSortChange(!checked); // Invert because switch shows "Posting" when checked
    }
  };

  // Custom action bar children with engagement metrics button, sort switch, and filters
  const actionBarChildren = (
    <div className="flex gap-2">
      {/* Filters - Only show if filter props are provided */}
      {filters && onFilterChange && onClearFilters && (
        <KolPostingLinksFilters
          filters={filters}
          onFilterChange={onFilterChange}
          onClearFilters={onClearFilters}
          hasActiveFilters={hasActiveFilters}
          isLoading={false}
        />
      )}

      {/* Sort Switch */}
      <div className="flex items-center gap-2 px-3 py-1 bg-muted/50 rounded-md border">
        <Clock className="h-3.5 w-3.5 text-muted-foreground" />
        <Label htmlFor="sort-switch" className="text-xs font-medium cursor-pointer">
          Sort by:
        </Label>
        <div className="flex items-center gap-1">
          <span className={`text-xs ${sortByCreatedTime ? 'text-foreground font-medium' : 'text-muted-foreground'}`}>
            Created
          </span>
          <Switch
            id="sort-switch"
            checked={!sortByCreatedTime}
            onCheckedChange={handleSortChange}
            className="scale-75"
          />
          <span className={`text-xs ${!sortByCreatedTime ? 'text-foreground font-medium' : 'text-muted-foreground'}`}>
            Posting
          </span>
        </div>
        <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
      </div>

      {!hasBuAccess && (
        <>
          <Button 
            variant="outline" 
            onClick={handleUpdateMetricsClick}
            className="h-8 text-foreground bg-background text-xs transition-colors hover:bg-muted px-3"
            disabled={isUpdatingMetrics}
            type="button"
          >
            {isUpdatingMetrics ? (
              <>
                <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                <span className="truncate">
                  Updating ({updateProgress?.current || 0}/{updateProgress?.total || 0})
                </span>
              </>
            ) : (
              <>
                <BarChart2 className="h-3.5 w-3.5 mr-1" />
                Update Engagement Metrics
              </>
            )}
          </Button>
          
          <AddKolTrackerDataDialog 
            onDataAdded={onDataChange}
            hasFullAccess={hasFullAccess}
            currentUserName={currentUserName}
          />
        </>
      )}

      {hasBuAccess && (
        <div className="text-sm text-red-600 font-medium bg-red-50 px-3 py-1 rounded-md">
          BU Access - Actions Disabled
        </div>
      )}

      {isProcessing && (
        <div className="flex items-center gap-2 text-xs text-blue-600 dark:text-blue-400">
          <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
          Saving changes...
        </div>
      )}

      {!hasFullAccess && (
        <div className="text-sm text-muted-foreground italic">
          Limited access mode
        </div>
      )}
    </div>
  );

  // Custom action bar with additional buttons for selected rows
  const customActionBarChildren = (selectedRowCount: number) => (
    <div className="flex gap-2 items-center">
      {actionBarChildren}
      {selectedRowCount > 0 && !hasBuAccess && (
        <>
          <Button 
            variant="outline" 
            onClick={handleEnableIntegrationSelected}
            className="h-8 text-foreground bg-background text-xs transition-colors hover:bg-muted px-3"
            disabled={isProcessing}
          >
            Enable Integration
          </Button>
          <Button 
            variant="outline" 
            onClick={handleDisableIntegrationSelected}
            className="h-8 text-foreground bg-background text-xs transition-colors hover:bg-muted px-3"
            disabled={isProcessing}
          >
            Disable Integration
          </Button>
        </>
      )}
      {/* Export button - always visible */}
      <KolPostingLinksExportDialog
        data={transformedData}
        selectedRows={selectedRows}
        totalCount={totalCount}
        currentFilters={filters}
      />
    </div>
  );

  return (
    <>
      <TopProgressBar
        isVisible={isUpdatingMetrics}
        current={updateProgress?.current || 0}
        total={updateProgress?.total || 0}
        url={updateProgress?.url}
        title="Updating Engagement Metrics"
      />
      
      <SearchableDataTable
        tableId="kol-posting-links"
        data={transformedData}
        columns={columnsWithNameHandler}
        loading={isLoading}
        searchPlaceholder="Search KOL Posting Links..."
        enableInstantSearch={false}
        searchTerm={searchTerm}
        onSearchChange={onSearchChange}
        onSearch={onSearch}
        enableRowSelection={!hasBuAccess} // Disable row selection for BU Access users
        selectedRows={selectedRows}
        onRowSelectionChange={setSelectedRows}
        enableColumnResizing={true}
        enableSorting={true}
        actionBarChildren={customActionBarChildren(selectedRows.length)}
        onDeleteSelected={handleDeleteSelected}
        emptyStateTitle="No KOL posting links data found"
        emptyStateDescription="Add some KOL posting links to get started"
        emptyStateAction={
          !hasBuAccess ? (
            <AddKolTrackerDataDialog 
              onDataAdded={onDataChange}
              hasFullAccess={hasFullAccess}
              currentUserName={currentUserName}
            />
          ) : null
        }
        isProcessing={isProcessing || isUpdatingMetrics}
        processingProgress={updateProgress}
        showPagination={true}
        pageSize={pageSize}
        currentPage={currentPage}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        totalItems={totalCount}
        dbSortColumn={dbSortColumn}
        dbSortDirection={dbSortDirection}
        onDbSort={handleDbSort}
        disableClientSorting={true}
      />
    </>
  );
}

export default KolPostingLinksTable;
